import React, { useState, useRef, useMemo } from "react";
import JoditEditor from "jodit-react";
import { Button } from "@components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";
import { HELP_DOC_STYLES } from "./sharedHelpStyles";

type Props = {
  handleSave: (
    doc: { title: string; content: string },
    isEditing: boolean,
  ) => void;
  initialDoc?: { title: string; content: string } | null;
};
const HelpUiPage: React.FC<Props> = ({ handleSave, initialDoc }) => {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const hasInitialized = useRef(false);

  const editor = useRef<any | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    // Ensure we're in browser environment and haven't initialized yet
    if (hasInitialized.current) return;

    if (typeof window !== "undefined" && typeof document !== "undefined") {
      // Longer delay to ensure DOM is fully ready, especially during hot reload
      const timer = setTimeout(() => {
        hasInitialized.current = true;
        setIsMounted(true);
      }, 100);

      return () => {
        clearTimeout(timer);
        // Close preview dialog on unmount to prevent errors during hot reload
        setIsPreviewOpen(false);
        // Reset initialization flag for clean remount
        hasInitialized.current = false;
      };
    }
  }, []);

  // Cleanup on unmount - close preview if open
  React.useEffect(() => {
    return () => {
      if (isPreviewOpen) {
        setIsPreviewOpen(false);
      }
    };
  }, [isPreviewOpen]);

  // Sync initialDoc when it changes
  React.useEffect(() => {
    if (initialDoc) {
      setTitle(initialDoc.title);
      setContent(initialDoc.content);
    } else {
      setTitle("");
      setContent("");
    }
  }, [initialDoc]);

  // Focus editor when initialDoc with content is loaded
  React.useEffect(() => {
    // Add all safety checks before attempting to focus
    if (typeof window === "undefined" || typeof document === "undefined")
      return;
    if (!isMounted) return;
    if (!editor.current || !initialDoc?.content) return;

    try {
      // Add a small delay to ensure editor is fully mounted
      const focusTimer = setTimeout(() => {
        if (editor.current && typeof document !== "undefined") {
          editor.current.focus();
        }
      }, 200);

      return () => clearTimeout(focusTimer);
    } catch (e) {
      console.error("Error focusing editor:", e);
    }
  }, [initialDoc, isMounted]);

  // Inject fullsize style once when component mounts
  React.useEffect(() => {
    // Only run once on client-side mount - no dependencies to prevent re-runs
    if (typeof window === "undefined" || typeof document === "undefined")
      return;

    // Add extra delay to ensure everything is ready
    const styleTimer = setTimeout(() => {
      try {
        const style = document.createElement("style");
        style.setAttribute("data-jodit-popup-style", "true");

        style.innerHTML = `
        .jodit-ui-dialog,
        .jodit-ui-popup,
        .jodit-popup,
        .jodit-popup__content,
        .jodit-ui-popup__content,
        .jodit-ui-dialog__content,
        .jodit-toolbar__box,
        .jodit-ui-group,
        .jodit-ui-button,
        .jodit-ui-dialog__title,
        .jodit-ui-form,
        .jodit-form__container {
          z-index: 999999 !important;
          pointer-events: auto !important;
        }

        .jodit-ui-dialog__overlay {
          pointer-events: none !important;
        }

        /* Comprehensive input field styles */
        .jodit-ui-input,
        .jodit-ui-input input,
        .jodit-ui-input textarea,
        .jodit-input,
        .jodit-ui-dialog input,
        .jodit-ui-popup input,
        .jodit-popup input,
        .jodit-ui-popup input[type="text"],
        .jodit-ui-popup input[type="url"],
        .jodit-ui-form input,
        .jodit-form__container input,
        .jodit-ui-block input,
        .jodit-ui-block textarea {
          pointer-events: auto !important;
          z-index: 9999999 !important;
          user-select: text !important;
          -webkit-user-select: text !important;
          touch-action: auto !important;
        }

        body.jodit_fullsize-box {
          overflow: hidden !important;
        }

        /* Fix unordered list bullets display */
        .jodit-wysiwyg ul {
          list-style-type: disc !important;
          list-style-position: inside !important;
          padding-left: 20px !important;
          margin: 10px 0 !important;
        }

        .jodit-wysiwyg ul li {
          display: list-item !important;
          list-style-type: disc !important;
        }

        .jodit-wysiwyg ol {
          list-style-type: decimal !important;
          list-style-position: inside !important;
          padding-left: 20px !important;
          margin: 10px 0 !important;
        }

        .jodit-wysiwyg ol li {
          display: list-item !important;
          list-style-type: decimal !important;
        }

        /* Heading styles */
        .jodit-wysiwyg h1 {
          font-size: 2em !important;
          font-weight: bold !important;
          margin: 0.67em 0 !important;
          line-height: 1.2 !important;
          display: block !important;
        }

        .jodit-wysiwyg h2 {
          font-size: 1.5em !important;
          font-weight: bold !important;
          margin: 0.75em 0 !important;
          line-height: 1.3 !important;
          display: block !important;
        }

        .jodit-wysiwyg h3 {
          font-size: 1.17em !important;
          font-weight: bold !important;
          margin: 0.83em 0 !important;
          line-height: 1.4 !important;
          display: block !important;
        }

        .jodit-wysiwyg h4 {
          font-size: 1em !important;
          font-weight: bold !important;
          margin: 1.12em 0 !important;
          line-height: 1.4 !important;
          display: block !important;
        }

        .jodit-wysiwyg h5 {
          font-size: 0.83em !important;
          font-weight: bold !important;
          margin: 1.5em 0 !important;
          line-height: 1.4 !important;
          display: block !important;
        }

        .jodit-wysiwyg h6 {
          font-size: 0.67em !important;
          font-weight: bold !important;
          margin: 1.67em 0 !important;
          line-height: 1.4 !important;
          display: block !important;
        }

        /* Blockquote styles */
        .jodit-wysiwyg blockquote {
          display: block !important;
          margin: 1em 0 !important;
          padding: 10px 20px !important;
          border-left: 4px solid #ccc !important;
          background-color: #f9f9f9 !important;
          font-style: italic !important;
          color: #666 !important;
        }

        .jodit-wysiwyg blockquote p {
          margin: 0 !important;
          display: block !important;
        }

        /* Code block styles */
        .jodit-wysiwyg pre {
          display: block !important;
          font-family: monospace !important;
          background-color: #f4f4f4 !important;
          border: 1px solid #ddd !important;
          border-radius: 4px !important;
          padding: 10px !important;
          margin: 10px 0 !important;
          overflow-x: auto !important;
          white-space: pre !important;
        }

        .jodit-wysiwyg code {
          font-family: monospace !important;
          background-color: #f4f4f4 !important;
          padding: 2px 4px !important;
          border-radius: 3px !important;
          font-size: 0.9em !important;
        }

        .jodit-wysiwyg pre code {
          background-color: transparent !important;
          padding: 0 !important;
        }

        /* Hide file-related options from link popup/context menu */
        .jodit-ui-popup button[aria-label*="file"],
        .jodit-ui-popup button[aria-label*="File"],
        .jodit-popup button[aria-label*="file"],
        .jodit-popup button[aria-label*="File"],
        .jodit-toolbar-button_file,
        .jodit-ui-button_file,
        button[data-ref="file"],
        .jodit-link-insert-actions button:has(svg[data-icon*="file"]),
        .jodit-ui-group button:has(svg[data-icon="file"]),
        button.jodit-ui-button:has(span:contains("file")),
        button.jodit-ui-button:has(span:contains("File")) {
          display: none !important;
          visibility: hidden !important;
        }
      `;

        if (document?.head) {
          document.head.appendChild(style);
        }
      } catch (e) {
        console.error("Error injecting Jodit styles:", e);
      }
    }, 150);

    return () => {
      clearTimeout(styleTimer);
    };
  }, []);

  // Memoize config to prevent re-creation on each render
  const config = useMemo(() => {
    // Triple check environment before creating config
    if (!isMounted) return null;
    if (typeof window === "undefined") return null;
    if (typeof document === "undefined") return null;
    if (!document.body) return null;

    try {
      return {
        readonly: false,
        placeholder: content ? "" : "Create help document here...",
        height: 550,
        width: "100%",
        toolbarSticky: true,
        toolbarAdaptive: true,
        // Paste configuration - disable dialog and auto-paste as HTML
        askBeforePasteHTML: false,
        askBeforePasteFromWord: false,
        defaultActionOnPaste: "insert_as_html",
        link: {
          target: "_blank",
          noFollowCheckbox: true,
          openInNewTabCheckbox: true,
          modeClassName: "input",
        },
        language: "en",
        i18n: {
          en: {
            "Drop image":
              'Drop image or click <br/><small style="color: #666; padding-left: 5px; padding-right: 5px;"></small>',
            "or click":
              '<small style="color: #666; padding-left: 10px; padding-right: 10px;"">Supported formats: JPG, PNG, JPEG, GIF (Max 2MB)</small>',
          },
        },
        uploader: {
          insertImageAsBase64URI: true,
          imagesExtensions: ["jpg", "png", "jpeg", "gif"],
          maxSize: 2 * 1024 * 1024,
        },
        image: {
          editSrc: false,
          useImageEditor: false,
          openOnDblClick: false,
        },
        showBlocks: {
          enable: false,
        },
        resizer: { showSize: true },
        allowResizeX: true,
        allowResizeY: true,
        events: {
          afterOpenPopup: (popup: any) => {
            // Ensure all input fields in popups are interactive and focusable
            setTimeout(() => {
              const inputs =
                popup?.container?.querySelectorAll("input, textarea");
              if (inputs && inputs.length > 0) {
                inputs.forEach((input: HTMLElement) => {
                  input.style.pointerEvents = "auto";
                  input.style.userSelect = "text";
                  input.removeAttribute("disabled");
                  input.removeAttribute("readonly");
                });
                // Auto-focus first input and make cursor visible
                const firstInput = inputs[0] as HTMLInputElement;
                if (firstInput) {
                  firstInput.focus();
                  firstInput.select();
                  // Force cursor visibility
                  firstInput.style.caretColor = "auto";
                }
              }

              // Remove any file-related buttons from link popup
              const fileButtons = popup?.container?.querySelectorAll(
                'button[aria-label*="file"], button[aria-label*="File"], button[data-ref="file"], .jodit-toolbar-button_file',
              );
              fileButtons?.forEach((btn: HTMLElement) => {
                btn.style.display = "none";
                btn.remove();
              });
            }, 100);
          },
          beforePaste: (event: ClipboardEvent) => {
            const items = event.clipboardData?.items;
            if (items) {
              for (const item of items) {
                if (item.type.indexOf("image") !== -1) {
                  const file = item.getAsFile();
                  if (file && file.size > 2 * 1024 * 1024) {
                    event.preventDefault();
                    alert(
                      "Image size must be less than 2MB.\n\nAllowed formats: JPG, PNG, JPEG, GIF",
                    );
                    return false;
                  }
                }
              }
            }
          },
          beforeFileOpen: (files: File[]) => {
            if (files && files[0].size > 2 * 1024 * 1024) {
              alert(
                "Image size must be less than 2MB.\n\nAllowed formats: JPG, PNG, JPEG, GIF",
              );
              return false;
            }
            return true;
          },
        },
        // Custom controls for preview
        controls: {
          ul: {
            list: undefined,
          },
          ol: {
            list: undefined,
          },
          preview: {
            exec: () => {
              setIsPreviewOpen(true);
            },
            tooltip: "Preview Document",
          },
          image: {
            tooltip: "Insert Image (Allowed: JPG, PNG, JPEG, GIF - Max 2MB)",
            popup: (editor: any, close: any) => {
              const form = editor.create.div("jodit-image-upload-only");
              form.innerHTML = `
                  <div style="padding: 15px;">
                    <label style="display: block; margin-bottom: 10px; font-weight: 500;">Upload Image</label>
                    <input type="file" accept="image/jpeg,image/jpg,image/png,image/gif" />
                    <div style="margin-top: 8px; font-size: 12px; color: #666;">
                      Allowed: JPG, PNG, JPEG, GIF (Max 2MB)
                    </div>
                  </div>
                `;

              const input = form.querySelector('input[type="file"]');
              input?.addEventListener("change", (e: any) => {
                const file = e.target.files?.[0];
                if (!file) return;

                // Validate size
                if (file.size > 2 * 1024 * 1024) {
                  alert("Image size must be less than 2MB");
                  return;
                }

                const reader = new FileReader();
                reader.onload = (event: any) => {
                  editor.selection.insertImage(event.target.result);
                  close();
                };
                reader.readAsDataURL(file);
              });

              return form;
            },
          },
        },
        popupRoot: document.body,
        buttons: [
          "paragraph",
          "|",
          "bold",
          "italic",
          "underline",
          "|",
          "font",
          "fontsize",
          "brush",
          "|",
          "ul",
          "ol",
          "|",
          "link",
          "image",
          "|",
          "align",
          "|",
          "undo",
          "redo",
          "align",
          "preview",
          "table",
        ],
        // Hide specific buttons
        removeButtons: [
          "video",
          "file",
          "iframe",
          "hr",
          "eraser",
          "copyformat",
          "symbol",
          "print",
          "about",
          "speechRecognize",
          "symbols",
          "fullsize",
          "dots",
        ],
        // Disable plugins for extra buttons
        disablePlugins: ["video", "speech-recognize"],
      };
    } catch (error) {
      console.error("Error creating Jodit config:", error);
      return null;
    }
  }, [isMounted]) as any;

  return (
    <>
      <style>{HELP_DOC_STYLES}</style>
      <div className="border-1 h-auto space-y-4 overflow-wrap pt-2">
        <div className="mb-4">
          <label className="block mb-1 text-sm font-medium">Page Title*</label>
          <input
            type="text"
            placeholder="Page Title (Plugin Help Document)"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full border border-secondary px-3 py-2 rounded-md text-sm bg-transparent focus:ring-secondary focus:ring-1"
          />
        </div>
        <div
          className="border rounded-md p-3 bg-white shadow-md w-[100%] relative"
          ref={wrapperRef}
        >
          {isMounted && config && (
            <JoditEditor
              key="jodit-editor"
              ref={editor}
              value={content}
              config={config}
              tabIndex={1}
              onBlur={(newContent: string) => setContent(newContent)}
            />
          )}
          {(!isMounted || !config) && (
            <div className="h-[550px] flex items-center justify-center text-gray-500">
              Loading editor...
            </div>
          )}
          <div className="w-full bg-white h-8 absolute bottom-0 left-0 z-[9999] pointer-events-none"></div>
        </div>

        <div className="flex justify-end items-center gap-1">
          <Button onClick={() => handleSave({ title, content }, !!initialDoc)}>
            {initialDoc ? "Update Help Document" : "Save Help Document"}
          </Button>
        </div>

        {/* Preview Dialog */}
        <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
          <DialogContent className="max-w-4xl max-h-[68vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{title || "Untitled Document"}</DialogTitle>
            </DialogHeader>
            <div className="border-t pt-4">
              {isMounted && content && (
                <div
                  className="prose max-w-none"
                  dangerouslySetInnerHTML={{ __html: content }}
                />
              )}
              {(!isMounted || !content) && (
                <p className="text-gray-500">No content to preview</p>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};

export default HelpUiPage;
